import React, { FC, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Autocomplete,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { debounce } from 'lodash';
import { isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { differenceInHours } from 'date-fns';
import { DateTimeRangePicker } from 'components/DateTimeRangePickerLocal/DateTimeRangePickerLocal';
import TextInput from 'components/TextInput/TextInput';
import { findErrorMessage } from 'helpers';
import { searchMerchants } from 'helpers/api';
import { isErrorResponse } from 'helpers/typeGuards';
import { useGetUserApplicationsQuery } from 'reduxState/store/application/api';
import { useCreateBoostedOfferCampaignsMutation } from 'reduxState/store/boostedOffers/api';
import { FundingSource } from 'reduxState/store/boostedOffers/types';
import { FUNDING_SOURCE_OPTIONS, SELECTABLE_FUNDING_SOURCES } from '../constants';
import { getAppOptions } from '../helpers';
import { formatLocalDateTime } from 'helpers/DateHelpers';

const isValidRate = (str: string) => /^\d*(\.\d+)?$/.test(str) && /\d/.test(str);

interface IDropdownOption {
  label: string;
  id: string;
}

interface ICampaignCreationData {
  ApplicationID: number;
  MerchantID: number;
  Kind: string;
  Rate: number;
  StartDate: string;
  EndDate: string;
  ModifiedAuthor: string;
  FundingSource: FundingSource;
}

interface IFailedCampaignCreationData {
  ApplicationID: number;
  MerchantID: number;
  Error: string;
}

interface IFormErrors {
  applications: string;
  merchants: string;
  kind: string;
  rate: string;
  startDate: string;
  endDate: string;
  fundingSource: string;
}

/**
 * CreateBoostedOfferCampaignForm is a React component that allows users to create boosted offer campaigns.
 * The component provides a form to input campaign details such as applications, merchants, rate,
 * start date, and end date. It also displays any failed campaign creations.
 *
 * @component
 * @example
 * return (
 *   <CreateBoostedOfferCampaignForm />
 * )
 */
const CreateBoostedOfferCampaignForm: FC = () => {
  const { user } = useAuth0();
  const { data: applicationsData } = useGetUserApplicationsQuery();
  const applications = applicationsData?.applications || [];
  const [merchantOptions, setMerchantOptions] = useState<IDropdownOption[]>([]);
  const [selectedAppOptions, setSelectedAppOptions] = useState<IDropdownOption[]>([]);
  const [selectedMerchantOptions, setSelectedMerchantOptions] = useState<IDropdownOption[]>([]);
  const [selectedKind, setSelectedKind] = useState('');
  const [rate, setRate] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [fundingSource, setFundingSource] = useState<FundingSource>('UNKNOWN');
  const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);
  const [failedCampaignsToCreate, setFailedCampaigns] = useState<IFailedCampaignCreationData[]>([]);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<IFormErrors>({
    applications: '',
    merchants: '',
    rate: '',
    startDate: '',
    endDate: '',
    kind: '',
    fundingSource: '',
  });
  const [appSearchValue, setAppSearchValue] = useState('');
  const [createBoostedOfferCampaigns, { isLoading: isCreateReqLoading }] = useCreateBoostedOfferCampaignsMutation();

  const renderFailedCampaignRow = ({ ApplicationID, MerchantID, Error }: IFailedCampaignCreationData) => {
    const appName = selectedAppOptions.find(option => option.id === ApplicationID.toString())?.label;
    const merchantName = selectedMerchantOptions.find(option => option.id === MerchantID.toString())?.label;
    if (!appName || !merchantName) return <></>;
    return (
      <TableRow key={`${appName}${merchantName}`}>
        <TableCell>{appName}</TableCell>
        <TableCell>{merchantName}</TableCell>
        <TableCell>{Error}</TableCell>
      </TableRow>
    );
  };

  const clearForm = () => {
    setMerchantOptions([]);
    setSelectedAppOptions([]);
    setSelectedMerchantOptions([]);
    setRate('');
    setStartDate(null);
    setEndDate(null);
    setSelectedKind('');
    setFundingSource('UNKNOWN');
    setIsMerchantsLoading(false);
    setFailedCampaigns([]);
    setError('');
    setFormErrors({
      applications: '',
      merchants: '',
      rate: '',
      startDate: '',
      endDate: '',
      kind: '',
      fundingSource: '',
    });
    setAppSearchValue('');
  };

  const handleMerchantSearch = async (query: string) => {
    if (query.length < 3) {
      return setMerchantOptions([]);
    }

    setIsMerchantsLoading(true);
    const results = await searchMerchants(query);

    if (isErrorResponse(results)) {
      return setError(findErrorMessage(results));
    }

    const merchantResults = results.filter(result => result.Type === 'merchant' && !result.Disabled);
    const merchantDropdownOptions: IDropdownOption[] = merchantResults.map(({ ID, Value }) => ({
      id: String(ID),
      label: Value,
    }));

    setMerchantOptions(merchantDropdownOptions);
    setIsMerchantsLoading(false);
  };

  const debouncedMerchantSearch = debounce(handleMerchantSearch, 300);

  const handleOnSubmit = async () => {
    const newFormErrors: Partial<IFormErrors> = {};
    const startsWithinThreeHrs = startDate && Math.abs(differenceInHours(startDate, new Date())) < 3;

    if (!rate || !isValidRate(rate)) {
      newFormErrors.rate = 'Invalid Rate provided. Rate must be a number, and may include a decimal value.';
    }

    if (!startDate) {
      newFormErrors.startDate = 'Campaign must have a start date.';
    } else if (startDate < new Date()) {
      newFormErrors.startDate = 'Start Date cannot be in the past.';
    } else if (startsWithinThreeHrs) {
      newFormErrors.startDate = 'Start Time must be at least three hours from now.';
    }

    if (!endDate) {
      newFormErrors.endDate = 'Campaign must have an end date.';
    } else if (startDate && endDate <= startDate) {
      newFormErrors.endDate = 'End Date must be after Start Date.';
    }

    if (!selectedAppOptions.length) {
      newFormErrors.applications = 'At least one application must be provided.';
    }
    if (!selectedMerchantOptions.length) {
      newFormErrors.merchants = 'At least one merchant must be provided.';
    }

    if (!selectedKind) {
      newFormErrors.kind = 'Kind must be provided.';
    }

    if (!SELECTABLE_FUNDING_SOURCES.includes(fundingSource)) {
      newFormErrors.fundingSource = 'Funding source must be provided.';
    }

    if (!isEqual(newFormErrors, {})) {
      return setFormErrors({ ...formErrors, ...newFormErrors });
    }

    if (
      !startDate ||
      !endDate ||
      !rate ||
      !selectedKind ||
      !SELECTABLE_FUNDING_SOURCES.includes(fundingSource) ||
      !selectedAppOptions.length ||
      !selectedMerchantOptions.length
    ) {
      return;
    }

    const campaignsToCreate: ICampaignCreationData[] = [];
    selectedAppOptions.forEach(app => {
      selectedMerchantOptions.forEach(merchant => {
        campaignsToCreate.push({
          ApplicationID: Number(app.id),
          MerchantID: Number(merchant.id),
          Rate: Number(rate),
          Kind: selectedKind,
          StartDate: startDate.toISOString(),
          EndDate: endDate.toISOString(),
          ModifiedAuthor: user!.email!,
          FundingSource: fundingSource,
        });
      });
    });

    setFailedCampaigns([]);
    setError('');

    try {
      const result = await createBoostedOfferCampaigns({
        campaigns: campaignsToCreate,
      }).unwrap();
      // If the selected app does not support boosted offers, the response will be an error message
      if (result?.message && result?.message === 'application does not support enhanced rates') {
        let errorMessage = '';
        // If there is only one campaign to create, show the error message for that campaign
        if (campaignsToCreate.length === 1) {
          errorMessage = `Failed to create the campaign: Application ${campaignsToCreate[0].ApplicationID} does not support enhanced rates.`;
        } else {
          errorMessage = 'Some of the selected applications do not support enhanced rates. Please check and try again.';
        }
        toast.error(errorMessage);
      } else {
        toast.success(`Campaign${campaignsToCreate.length > 1 ? 's' : ''} created successfully!`);
      }
      clearForm();
      setFailedCampaigns([]);
    } catch (errorResponse) {
      let errorMessage = 'Failed to create boosted offer campaigns.';
      const error = errorResponse.data;
      if (error?.ErrorMessage) {
        errorMessage = error.ErrorMessage[0].toUpperCase() + error.ErrorMessage.slice(1);
        if (error?.Failed) {
          setFailedCampaigns(error.Failed as IFailedCampaignCreationData[]);
        }
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="flex flex-col items-center flex-1 mt-12">
      <div className="max-w-[1400px] w-full h-fit flex flex-col font-montserrat bg-white rounded-md p-6 shadow-lg">
        <h4 className="w-full mb-2.5">Create Boosted Offer Campaign</h4>
        <div className="flex flex-col w-full">
          <Autocomplete
            sx={{ my: 1 }}
            id="applications"
            options={getAppOptions(applications)}
            value={selectedAppOptions}
            loading={!getAppOptions(applications).length}
            multiple
            filterSelectedOptions
            isOptionEqualToValue={(option, value) => option.id === value.id}
            inputValue={appSearchValue}
            onInputChange={(e, value, reason) => {
              if (reason !== 'reset') setAppSearchValue(value);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Applications"
                placeholder="Select all applications"
                error={Boolean(formErrors.applications)}
                helperText={formErrors.applications}
              />
            )}
            onChange={(e, value) => {
              setSelectedAppOptions(value);
              setFormErrors({ ...formErrors, applications: '' });
            }}
            disableCloseOnSelect
          />
          <Autocomplete
            sx={{ my: 1 }}
            multiple
            id="merchants"
            value={selectedMerchantOptions}
            options={[...selectedMerchantOptions, ...merchantOptions]}
            loading={isMerchantsLoading}
            filterSelectedOptions
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={x => x}
            renderInput={params => (
              <TextField
                {...params}
                label="Merchants"
                placeholder="Select all merchants"
                error={Boolean(formErrors.merchants)}
                helperText={formErrors.merchants}
              />
            )}
            onInputChange={(e, value) => debouncedMerchantSearch(value)}
            onChange={(e, value) => {
              setSelectedMerchantOptions(value);
              setFormErrors({ ...formErrors, merchants: '' });
            }}
          />
          <div className="flex flex-row gap-2 items-center flex-wrap md:flex-nowrap">
            <FormControl error={Boolean(formErrors.kind)} fullWidth className="my-1 w-full md:w-[50%]">
              <InputLabel className="pl-1 pr-2 bg-white " id="kind-label">
                Kind
              </InputLabel>
              <Select
                notched
                variant="outlined"
                labelId="kind-label"
                value={selectedKind}
                defaultValue=""
                inputProps={{ 'data-testid': 'kind-select' }}
                onChange={e => {
                  setSelectedKind(e.target.value as string);
                  setFormErrors({ ...formErrors, kind: '' });
                }}
              >
                <MenuItem value="MULTIPLIER">Multiplier</MenuItem>
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              </Select>
              {Boolean(formErrors.kind) && <FormHelperText>{formErrors.kind}</FormHelperText>}
            </FormControl>
            <div className="w-full md:w-[50%]">
              <TextInput
                label="Rate"
                value={rate}
                handleChange={val => {
                  setRate(val);
                  setFormErrors({ ...formErrors, rate: '' });
                }}
                hasError={Boolean(formErrors.rate)}
                errorText={formErrors.rate}
              />
            </div>
          </div>
          <FormControl error={Boolean(formErrors.fundingSource)} fullWidth className="my-[8px]">
            <InputLabel className="pl-1 pr-2 bg-white " id="funding-source-label">
              Funded By
            </InputLabel>
            <Select
              notched
              variant="outlined"
              labelId="funding-source-label"
              value={fundingSource}
              defaultValue=""
              inputProps={{ 'data-testid': 'funding-source-select' }}
              onChange={e => {
                setFundingSource(e.target.value as FundingSource);
                setFormErrors({ ...formErrors, fundingSource: '' });
              }}
            >
              {FUNDING_SOURCE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {Boolean(formErrors.fundingSource) && <FormHelperText>{formErrors.fundingSource}</FormHelperText>}
          </FormControl>
          <DateTimeRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={value => {
              setStartDate(value);
              setFormErrors({ ...formErrors, startDate: '' });
            }}
            onEndDateChange={value => {
              setEndDate(value);
              setFormErrors({ ...formErrors, endDate: '' });
            }}
            startDateError={Boolean(formErrors.startDate)}
            endDateError={Boolean(formErrors.endDate)}
            startDateHelperText={formErrors.startDate}
            endDateHelperText={formErrors.endDate}
            startDateDisabled={isCreateReqLoading}
            endDateDisabled={isCreateReqLoading}
          />
          <div className="flex flex-row gap-2">
            {startDate && (
              <div className="w-[230px]">
                <p className="text-xs italic mt-2">
                  Pacific: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Los_Angeles')}
                </p>
                <p className="text-xs italic">
                  Mountain: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Denver')}
                </p>
                <p className="text-xs italic">
                  Central: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Chicago')}
                </p>
                <p className="text-xs italic">
                  Eastern: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/New_York')}
                </p>
              </div>
            )}
            {endDate && (
              <div className="w-[230px]">
                <p className="text-xs italic mt-2">
                  Pacific: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Los_Angeles')}
                </p>
                <p className="text-xs italic">
                  Mountain: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Denver')}
                </p>
                <p className="text-xs italic">
                  Central: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Chicago')}
                </p>
                <p className="text-xs italic">
                  Eastern: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/New_York')}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end w-full h-20 mt-4">
            <div className="flex items-center justify-center w-full h-auto">
              {error && (
                <div>
                  <p style={{ color: 'red' }}>{error}</p>
                </div>
              )}
            </div>
            <Button
              className="min-h-[50px] max-h-[50px] px-5 mr-2.5 bg-light-grey text-dark-grey font-semibold hover:bg-dark-purple hover:text-white"
              onClick={clearForm}
            >
              CLEAR
            </Button>
            <Button
              className={`min-h-[50px] max-h-[50px] px-5 font-semibold leading-snug ${
                isCreateReqLoading
                  ? 'bg-light-grey2 text-light-purple'
                  : 'bg-light-purple text-white hover:bg-dark-purple'
              }`}
              disabled={isCreateReqLoading}
              onClick={handleOnSubmit}
            >
              {isCreateReqLoading ? 'Creating Campaign...' : 'Create Campaign'}
            </Button>
          </div>
          {!!failedCampaignsToCreate.length && (
            <Paper variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Application</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failedCampaignsToCreate.map(failedCampaign => renderFailedCampaignRow(failedCampaign))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateBoostedOfferCampaignForm;
